import {
	allowedTarget,
	type BrowserTask,
	fieldLength,
	fieldRequired,
	type PlatformContract,
	type PublishPayload,
	schedulingIssues,
	UNATTENDED_PLATFORMS,
	validatePayload,
} from "@ondit/publishing";
import {
	Button,
	ChipConfirmModal,
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	Label,
	Textarea,
} from "@ondit/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useAccess } from "~/core/access/index";
import type { DistributionSource } from "~/core/api/distribution";
import { ApiError, api } from "~/core/api/index";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys } from "~/core/query";
import { Hint } from "~/core/ui/page";
import { SearchSelect } from "~/core/ui/search-select";
import { InstallPublisher } from "~/modules/analytics/features/platform-publishing/install";
import { PlatformPreview } from "~/modules/analytics/features/platform-publishing/preview";
import { articleMarkdown } from "~/modules/analytics/lib/distribution";
import { openPublisher, usePublisherExtension } from "./extension";
import { ResultLink } from "./result-link";

export function PlatformComposer({
	source,
	platform,
	entityId,
	onStageChange,
	taskId,
	expandedPreview = false,
	readOnly = false,
}: {
	source: DistributionSource;
	platform: PlatformContract;
	entityId: string;
	onStageChange?: (finishing: boolean) => void;
	taskId?: string;
	expandedPreview?: boolean;
	readOnly?: boolean;
}) {
	const recoveryKey = `ondit.publisher.${entityId}.${source.id}.${source.revision}.${platform.id}`;
	const t = useT();
	const extension = usePublisherExtension();
	const queryClient = useQueryClient();
	const tasks = useQuery({
		queryKey: [...keys.entity(entityId), "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
		refetchInterval: 5000,
	});
	const initialized = useRef(false);
	const observedTask = useRef("");
	const [handedOff, setHandedOff] = useState(false);
	useEffect(() => onStageChange?.(handedOff), [handedOff, onStageChange]);
	const [error, setError] = useState("");
	const { locale } = useI18n();
	const { editor: canEdit } = useAccess();
	const editor = canEdit && !readOnly;
	const [payload, setPayload] = useState<PublishPayload>(() => {
		try {
			const cached = JSON.parse(sessionStorage.getItem(recoveryKey) ?? "null");
			if (
				cached?.platform === platform.id &&
				cached?.source?.revision === source.revision &&
				cached?.fields &&
				Array.isArray(cached.media)
			)
				return cached;
		} catch {}
		const body = ["article", "newsletter", "discussion"].includes(platform.preview.layout)
			? articleMarkdown(source).replace(/^# [^\n]*\n\n/, "")
			: [source.title, ...source.blocks.filter((b) => b.type === "answer").map((b) => b.text), source.canonical].join(
					"\n\n",
				);
		const fields: Record<string, string> = {};
		for (const f of platform.preview.fields)
			fields[f.key] =
				f.key === "body"
					? body
					: f.key === "title"
						? source.title
						: f.key === "canonical"
							? source.canonical
							: f.key === "status"
								? "draft"
								: "";
		return {
			version: 1,
			platform: platform.id,
			fields,
			media: [],
			source: { page_id: source.id, revision: source.revision, canonical: source.canonical },
			target_url: platform.entry_url,
		};
	});
	useEffect(() => {
		try {
			sessionStorage.setItem(recoveryKey, JSON.stringify(payload));
		} catch {}
	}, [recoveryKey, payload]);
	const [task, setTask] = useState<BrowserTask | null>(null);
	const contentLocked = task?.schedule_state === "waiting" || task?.schedule_state === "running";
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState("");
	const [scheduleOpen, setScheduleOpen] = useState(false);
	const [scheduleAt, setScheduleAt] = useState("");
	const [confirm, setConfirm] = useState(false);
	const [resultUrl, setResultUrl] = useState("");
	const apiPublish = platform.methods.includes("api");
	const browserPublish = platform.methods.includes("browser");
	const [credentialId, setCredentialId] = useState("");
	const accounts = (tasks.data?.accounts ?? []).filter((a) => a.provider_id === platform.credential_provider);
	const [recoveredRequest] = useState(() => {
		try {
			const cached = JSON.parse(sessionStorage.getItem(`${recoveryKey}.request`) ?? "null");
			if (typeof cached?.key === "string" && typeof cached?.snapshot === "string")
				return cached as { key: string; snapshot: string };
		} catch {}
		return { key: crypto.randomUUID(), snapshot: "" };
	});
	const request = useRef(recoveredRequest);
	const issues = validatePayload(payload);
	const dirty =
		!task || ["failed", "cancelled"].includes(task.state) || JSON.stringify(task.payload) !== JSON.stringify(payload);
	useEffect(() => {
		const data = tasks.data;
		if (!data) return;
		if (!initialized.current && apiPublish) {
			const first = data.accounts.find((a) => a.provider_id === platform.credential_provider);
			if (first) {
				setCredentialId(first.id);
				// A WordPress account names the site the task is bound to. An
				// OAuth account names a person, and adopting that as the target
				// leaves a task whose address is a user id.
				if (first.url && allowedTarget(platform, first.url))
					setPayload((p) => ({ ...p, target_url: first.url as string }));
			}
		}
		const explicit = taskId ?? new URLSearchParams(location.search).get("task");
		let id = task?.id ?? explicit;
		try {
			id ??= sessionStorage.getItem(`${recoveryKey}.task`);
		} catch {}
		const old = data.tasks.find(
			(row) =>
				row.id === id &&
				row.payload.platform === platform.id &&
				row.payload.source.page_id === source.id &&
				(readOnly || row.payload.source.revision === source.revision),
		);
		if (old) {
			setTask(old);
			if (!initialized.current && explicit) setPayload(old.payload);
			if (old.published_url) setResultUrl(old.published_url);
			const stateKey = `${old.id}:${old.state}`;
			if (
				!expandedPreview &&
				observedTask.current !== stateKey &&
				["filled", "user_reported", "published", "remote_draft", "unknown"].includes(old.state)
			)
				setHandedOff(true);
			observedTask.current = stateKey;
		}
		initialized.current = true;
	}, [
		tasks.data,
		task?.id,
		platform,
		apiPublish,
		source.id,
		source.revision,
		recoveryKey,
		taskId,
		expandedPreview,
		readOnly,
	]);
	async function run(fn: () => Promise<void>) {
		setBusy(true);
		setError("");
		try {
			await fn();
		} catch (e) {
			setError(
				e instanceof ApiError
					? e.localized(locale)
					: e instanceof Error && e.message === "publisher_unavailable"
						? t("publishFlow.connectionLost")
						: String(e),
			);
		} finally {
			setBusy(false);
		}
	}
	async function save() {
		if (!editor || contentLocked) return null;
		if (issues.length) {
			toast.error(t("publishing.platform.invalid"));
			return null;
		}
		const snapshot = JSON.stringify(payload);
		if (["failed", "cancelled"].includes(task?.state ?? "") || request.current.snapshot !== snapshot)
			request.current = { key: crypto.randomUUID(), snapshot };
		try {
			sessionStorage.setItem(`${recoveryKey}.request`, JSON.stringify(request.current));
		} catch {}
		const row = await api.createPlatformTask(entityId, request.current.key, payload);
		try {
			sessionStorage.setItem(`${recoveryKey}.task`, row.id);
		} catch {}
		setTask(row);
		void queryClient.invalidateQueries({ queryKey: [...keys.entity(entityId), "platform-tasks"] });
		setNotice(t("publishing.platform.saved"));
		return row;
	}
	async function send() {
		const row = dirty ? await save() : task;
		if (!row) return;
		await openPublisher(row, locale);
		setHandedOff(true);
		setNotice("");
	}
	const change = (field: string, value: string) =>
		setPayload((p) => ({
			...p,
			fields: { ...p.fields, [field]: value },
			...(field === "destination" && value ? { target_url: value } : {}),
		}));
	const renderField = (f: PlatformContract["preview"]["fields"][number]) => {
		const value = payload.fields[f.key] ?? "";
		const length = fieldLength(f, value);
		const invalid = issues.includes(f.key);
		const over = f.max_length !== null ? length - f.max_length : 0;
		const feedbackId = `platform-${f.key}-feedback`;
		return (
			<div className="field" key={f.key}>
				<div className="publisher-field-label">
					<Label htmlFor={`platform-${f.key}`}>
						{pick(locale, f.label)}
						{fieldRequired(f, payload.fields) ? " *" : ""}
					</Label>
					<Hint text={t(f.transfer === "guided" ? "publishing.platform.guided" : "publishing.platform.automatic")} />
				</div>
				{f.kind === "select" ? (
					<SearchSelect
						label={pick(locale, f.label)}
						placeholder={t("publishing.platform.required")}
						searchLabel={pick(locale, f.label)}
						emptyLabel={t("table.noMatch")}
						disabled={!editor || busy || contentLocked}
						value={value}
						onChange={(v) => change(f.key, v)}
						options={(f.options ?? []).map((v) => ({ value: v, label: pick(locale, f.option_labels?.[v]) ?? v }))}
					/>
				) : f.kind === "textarea" ? (
					<Textarea
						id={`platform-${f.key}`}
						rows={f.key === "body" ? 12 : 3}
						value={value}
						onChange={(e) => change(f.key, e.target.value)}
						aria-invalid={invalid}
						aria-describedby={feedbackId}
					/>
				) : (
					<Input
						id={`platform-${f.key}`}
						value={value}
						onChange={(e) => change(f.key, e.target.value)}
						aria-invalid={invalid}
						aria-describedby={feedbackId}
					/>
				)}
				<div
					id={feedbackId}
					className={`publisher-field-feedback${invalid ? " publisher-error" : " hint"}`}
					aria-live="polite"
				>
					{invalid && (
						<span>
							{t(
								over > 0
									? "publishFlow.tooLong"
									: !value.trim()
										? "publishFlow.fieldRequired"
										: f.min_length !== undefined && length < f.min_length
											? "publishing.platform.tooShort"
											: "publishing.platform.invalidField",
								{ count: over > 0 ? over : (f.min_length ?? 0) },
							)}
						</span>
					)}
					{f.max_length !== null && (
						<span className="publisher-count">
							{length} / {f.max_length}
						</span>
					)}
				</div>
			</div>
		);
	};

	return (
		<div className={`stack publisher-editor${expandedPreview ? " publishing-content-editor-expanded" : ""}`}>
			<div className="publisher-step-heading">
				<h3>
					{t(
						task && ["published", "user_reported"].includes(task.state)
							? "publishFlow.recorded"
							: handedOff
								? "publishFlow.finish"
								: "publishFlow.review",
						{ platform: platform.name },
					)}
					<Hint
						text={t(
							apiPublish
								? browserPublish
									? "publishFlow.bothMethods"
									: "publishFlow.apiMethod"
								: "publishFlow.reviewHelp",
						)}
					/>
				</h3>
				{task &&
					!apiPublish &&
					!["cancelled", "sending", "unknown", "published", "remote_draft"].includes(task.state) && (
						<div className="platform-actions">
							<ResultLink
								title={source.title}
								value={resultUrl}
								disabled={!editor || busy}
								onSave={async (url) => {
									await api.reportPlatformTask(entityId, task.id, "user_reported", url);
									setResultUrl(url);
									setTask({ ...task, state: "user_reported", published_url: url });
									setHandedOff(true);
									await queryClient.invalidateQueries({ queryKey: [...keys.entity(entityId), "platform-tasks"] });
								}}
							/>
						</div>
					)}
				{handedOff && (
					<Button variant="ghost" onClick={() => setHandedOff(false)}>
						{t("publishFlow.edit")}
					</Button>
				)}
			</div>
			{platform.browser.instructions && <p role="note">{pick(locale, platform.browser.instructions)}</p>}
			{!expandedPreview && !apiPublish && extension.status !== "connected" && (
				<section className="publisher-connection" aria-live="polite">
					<div>
						<strong>{t(`publishFlow.extension.${extension.status}`)}</strong>
						<p className="hint">{t("publishFlow.installHelp")}</p>
					</div>
					<div className="platform-actions">
						<InstallPublisher />
						<Button variant="default" disabled={extension.status === "checking"} onClick={extension.check}>
							{t("publishFlow.recheck")}
						</Button>
					</div>
				</section>
			)}
			{handedOff && (
				<section className="publisher-next">
					<p role="status">{t(`publishing.platform.state.${task?.state ?? "ready"}`)}</p>
					{!apiPublish && task?.state !== "user_reported" && (
						<ol>
							<li>{t("publishFlow.inAssistant")}</li>
							<li>{t("publishFlow.onPlatform")}</li>
							<li>{t("publishFlow.returnLink")}</li>
						</ol>
					)}
					{task?.published_url && (
						<a href={task.published_url} target="_blank" rel="noreferrer">
							{t("publishFlow.openResult")}
						</a>
					)}
				</section>
			)}
			<div hidden={handedOff}>
				<fieldset disabled={!editor || busy || contentLocked} className="bare-fieldset platform-fields">
					{platform.preview.fields
						.filter(
							(f) =>
								f.key !== "body" &&
								(fieldRequired(f, payload.fields) || ["title", "status", "destination"].includes(f.key)),
						)
						.map(renderField)}
				</fieldset>
				<div
					className={`platform-composer platform-composer-aligned${expandedPreview ? " platform-composer-expanded" : ""}`}
				>
					<fieldset disabled={!editor || busy || contentLocked} className="bare-fieldset publisher-body-field">
						{platform.preview.fields.filter((f) => f.key === "body").map(renderField)}
					</fieldset>
					<div className="publisher-preview">
						<div className="publisher-field-label">
							<span>{t("publishing.platform.preview")}</span>
							<Hint text={t("publishing.platform.previewHelp")} />
						</div>
						<PlatformPreview platform={platform} payload={payload} externalHeading />
					</div>
				</div>
				<fieldset disabled={!editor || busy || contentLocked} className="bare-fieldset platform-fields">
					<details
						open={
							issues.includes("media") ||
							platform.preview.media?.required ||
							platform.preview.fields.some(
								(f) =>
									issues.includes(f.key) &&
									!fieldRequired(f, payload.fields) &&
									!["title", "body", "status", "destination"].includes(f.key),
							)
						}
					>
						<summary>
							{t(platform.preview.media?.required ? "publishFlow.mediaSettings" : "publishFlow.optional")}
						</summary>
						{platform.preview.media?.required && <p className="hint">{t("publishing.platform.mediaRequired")}</p>}
						<div className="platform-fields">
							{platform.preview.fields
								.filter(
									(f) =>
										!fieldRequired(f, payload.fields) && !["title", "body", "status", "destination"].includes(f.key),
								)
								.map(renderField)}
						</div>
						{platform.preview.media && (
							<div className="field">
								<Label htmlFor="platform-media">{t("publishing.platform.media")}</Label>
								<Textarea
									id="platform-media"
									aria-invalid={issues.includes("media")}
									value={payload.media.map((m) => m.url).join("\n")}
									onChange={(e) =>
										setPayload((p) => ({
											...p,
											media: e.target.value
												.split("\n")
												.filter(Boolean)
												.map((url) => ({ url, alt: p.media.find((m) => m.url === url)?.alt ?? "" })),
										}))
									}
								/>
								<p className="hint">{t("publishing.platform.mediaHelp")}</p>
								{(() => {
									const rule = platform.preview.media;
									const limits = [
										rule.max_count !== undefined && t("publishing.platform.mediaMax", { count: rule.max_count }),
										rule.min_count !== undefined && t("publishing.platform.mediaMin", { count: rule.min_count }),
										rule.video_alone && t("publishing.platform.mediaVideoAlone"),
										rule.no_video && t("publishing.platform.mediaNoVideo"),
									].filter(Boolean);
									return limits.length > 0 && <p className="hint">{limits.join(" · ")}</p>;
								})()}
								{issues.includes("media") && (
									<p role="alert" className="publisher-error">
										{t("publishing.platform.invalidField")}
									</p>
								)}
								{payload.media.map((m, i) => (
									<div className="field" key={m.url}>
										<Label htmlFor={`media-alt-${i}`}>
											{t("publishing.platform.alt")} · {i + 1}
										</Label>
										<Input
											id={`media-alt-${i}`}
											value={m.alt}
											onChange={(e) =>
												setPayload((p) => ({
													...p,
													media: p.media.map((item, n) => (n === i ? { ...item, alt: e.target.value } : item)),
												}))
											}
										/>
									</div>
								))}
							</div>
						)}
					</details>
				</fieldset>
			</div>
			{apiPublish && (
				<section className="stack">
					<h3>{t("publishing.platform.account")}</h3>
					{accounts.length > 0 && (
						<SearchSelect
							label={t("publishing.platform.account")}
							placeholder={t("publishing.platform.required")}
							searchLabel={t("publishing.platform.account")}
							emptyLabel={t("table.noMatch")}
							disabled={!editor || busy || contentLocked}
							value={credentialId}
							onChange={(id) => {
								const url = accounts.find((a) => a.id === id)?.url;
								setCredentialId(id);
								if (url && allowedTarget(platform, url)) setPayload((p) => ({ ...p, target_url: url }));
							}}
							options={accounts.map((a) => ({ value: a.id, label: a.name }))}
						/>
					)}
					{platform.credential_kind === "oauth" && platform.credential_provider ? (
						// The Integrations page is where a person browses providers; a
						// platform that needs one grant does not need to be browsed for.
						<Button
							variant="ghost"
							className="self-start"
							disabled={!editor || busy || contentLocked}
							onClick={() =>
								void run(async () => {
									const { url } = await api.connectCredential(
										entityId,
										platform.credential_provider!,
										`${location.origin}${location.pathname}${location.search}`,
									);
									location.assign(url);
								})
							}
						>
							{t("publishing.platform.connectAccount")}
						</Button>
					) : (
						<Link
							className="self-start"
							to={`/w/${entityId}/settings/integrations/${platform.id}?connect=service-account`}
						>
							{t("publishing.platform.connectAccount")}
						</Link>
					)}
				</section>
			)}
			{error && (
				<p role="alert" className="publisher-error">
					{error}
				</p>
			)}
			{tasks.error && (
				<p role="alert" className="publisher-error">
					{t("publishFlow.loadFailed")}
				</p>
			)}
			<div className="platform-actions publisher-footer">
				{issues.length > 0 && !handedOff && (
					<p role="status" className="publisher-error publisher-validation-summary">
						{t("publishFlow.checkFields")}
					</p>
				)}
				<div className="platform-actions">
					<Button
						variant="default"
						disabled={
							!editor || busy || tasks.isPending || !!tasks.error || contentLocked || issues.length > 0 || !dirty
						}
						onClick={() =>
							void run(async () => {
								await save();
							})
						}
					>
						{t("publishing.platform.save")}
					</Button>
					{UNATTENDED_PLATFORMS.includes(platform.id) && (
						<Button
							variant="default"
							disabled={
								!editor ||
								tasks.isPending ||
								!!tasks.error ||
								busy ||
								issues.length > 0 ||
								extension.status !== "connected" ||
								(!dirty && !["ready", "filled"].includes(task?.state ?? "")) ||
								contentLocked
							}
							onClick={() => setScheduleOpen(true)}
						>
							{t("publishFlow.schedule")}
						</Button>
					)}
					<div className="platform-publish-split">
						<Button
							variant="primary"
							disabled={
								!editor ||
								tasks.isPending ||
								!!tasks.error ||
								busy ||
								contentLocked ||
								issues.length > 0 ||
								(apiPublish && !credentialId) ||
								(!apiPublish && extension.status !== "connected") ||
								(!dirty && !["ready", "filled"].includes(task?.state ?? ""))
							}
							onClick={() => (apiPublish ? setConfirm(true) : void run(send))}
						>
							{busy
								? t("publishFlow.working")
								: t(apiPublish ? "publishing.platform.publish" : handedOff ? "publishFlow.reopen" : "publishFlow.go", {
										platform: platform.name,
									})}
						</Button>
						{/* A platform with both mechanisms publishes by API — that is what the
					    button does. The browser is still there for the parts an API does not
					    carry (a video the brand uploads itself, a field nobody exposed), so
					    it is the alternative rather than a second control of equal weight. */}
						{apiPublish && browserPublish && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="primary"
										aria-label={t("publishFlow.otherMethods")}
										disabled={!editor || busy || tasks.isPending || !!tasks.error || contentLocked}
									>
										<ChevronDown aria-hidden />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem
										disabled={
											extension.status !== "connected" ||
											issues.length > 0 ||
											(!dirty && !["ready", "filled"].includes(task?.state ?? ""))
										}
										onSelect={() => void run(send)}
									>
										{t(handedOff ? "publishFlow.reopen" : "publishFlow.go", { platform: platform.name })}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
				</div>
			</div>
			{task && ["waiting", "attention"].includes(task.schedule_state ?? "") && (
				<div className="platform-actions">
					{task?.schedule_state === "waiting" && (
						<>
							<span>
								{t("publishFlow.scheduleSaved", { time: new Date(task.scheduled_at!).toLocaleString(locale) })}
							</span>
							<Button
								variant="ghost"
								disabled={!editor || busy}
								onClick={() =>
									void run(async () => {
										const result = await api.cancelPlatformSchedule(entityId, task.id, extension.device);
										setTask(result);
										setNotice(t("publishFlow.scheduleCancelled"));
										void tasks.refetch();
									})
								}
							>
								{t("publishFlow.scheduleCancel")}
							</Button>
						</>
					)}
					{task?.schedule_state === "attention" && <p role="alert">{t("publishFlow.scheduleAttention")}</p>}
				</div>
			)}
			<ChipModal open={scheduleOpen} onOpenChange={setScheduleOpen} srTitle={t("publishFlow.schedule")}>
				<ChipModalHeader onClose={() => setScheduleOpen(false)} closeAriaLabel={t("common.close")}>
					{t("publishFlow.schedule")}
				</ChipModalHeader>
				<ChipModalBody>
					{error && (
						<ChipModalField type="custom" title="">
							<p role="alert" className="publisher-error">
								{error}
							</p>
						</ChipModalField>
					)}
					<ChipModalField type="custom" title={t("publishFlow.scheduleAt")}>
						<Input
							type="datetime-local"
							aria-label={t("publishFlow.scheduleAt")}
							value={scheduleAt}
							disabled={!editor || busy}
							onChange={(e) => setScheduleAt(e.target.value)}
						/>
					</ChipModalField>
					<ChipModalField type="custom" title="">
						<p>{t("publishFlow.scheduleHelp")}</p>
						<p>{t("publishFlow.scheduleLimits")}</p>
						{schedulingIssues(payload).length > 0 && (
							<p role="alert">
								{t("publishFlow.scheduleFields", {
									fields:
										schedulingIssues(payload)
											.map((key) => platform.preview.fields.find((f) => f.key === key))
											.filter(Boolean)
											.map((f) => pick(locale, f!.label))
											.join(", ") || t("publishing.platform.media"),
								})}
							</p>
						)}
					</ChipModalField>
				</ChipModalBody>
				<ChipModalFooter
					onCancel={() => setScheduleOpen(false)}
					cancelLabel={t("common.cancel")}
					primaryAction={{
						label: t("publishFlow.schedule"),
						disabled:
							schedulingIssues(payload).length > 0 ||
							!editor ||
							busy ||
							!scheduleAt ||
							new Date(scheduleAt).getTime() <= Date.now(),
						onClick: () =>
							void run(async () => {
								const row = dirty ? await save() : task;
								if (!row) return;
								await openPublisher(row, locale, new Date(scheduleAt).toISOString());
								setScheduleOpen(false);
								setNotice(t("publishFlow.schedulePending"));
							}),
					}}
				/>
			</ChipModal>

			{notice && <p role="status">{notice}</p>}

			<ChipConfirmModal
				open={confirm}
				onOpenChange={setConfirm}
				title={t("publishing.platform.publish", { platform: platform.name })}
				text={t("publishing.platform.publishConfirm", { platform: platform.name })}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("publishing.platform.confirm"),
					variant: "primary",
					disabled: busy || !editor,
					onClick: () =>
						void run(async () => {
							setConfirm(false);
							const row = dirty ? await save() : task;
							if (!row) return;
							const result = await api.publishPlatformTask(entityId, row.id, credentialId);
							setTask({ ...row, ...result });
							setNotice(t(`publishing.platform.state.${result.state}`));
						}),
				}}
			>
				<p className="break-words">
					{payload.target_url} · {payload.fields.status || "draft"}
				</p>
			</ChipConfirmModal>
		</div>
	);
}
